import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import ArticleCard from "../components/ArticleCard";
import BannerAd from "../components/BannerAd";
import { fetchArticles, fileUrl } from "../api";
import { Play } from "lucide-react";

const SkeletonCard = () => (
  <div className="bg-white border border-sq rounded-sm overflow-hidden animate-pulse">
    <div className="w-full h-52 bg-gray-200" />
    <div className="p-5 space-y-3">
      <div className="h-3 w-20 bg-gray-200 rounded" />
      <div className="h-5 w-3/4 bg-gray-200 rounded" />
      <div className="h-4 w-full bg-gray-100 rounded" />
    </div>
  </div>
);

const HomePage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchArticles({ limit: 100 })
      .then(setArticles)
      .catch(() => setArticles([]))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </div>
    );
  }

  if (articles.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-20 text-center text-gray-500">
        Nessun articolo disponibile.
      </div>
    );
  }

  const featured = articles.filter((a) => a.featured);
  const lead = featured[0] || articles[0];
  const secondary = featured.filter((a) => a.id !== lead.id).slice(0, 2);
  const rest = articles.filter((a) => a.id !== lead.id);
  const firstHalf = rest.slice(0, 4);
  const secondHalf = rest.slice(4);

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 fade-in">
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
        <Link
          to={`/articolo/${lead.slug}`}
          className="lg:col-span-2 group relative rounded-sm overflow-hidden card-img-wrap min-h-[360px] flex items-end"
        >
          <img
            src={fileUrl(lead.image)}
            alt={lead.title}
            className="card-img-zoom absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
          {lead.videoUrl && (
            <span className="absolute top-5 left-5 bg-sq-orange text-white text-[11px] uppercase tracking-wide font-semibold px-2.5 py-1 rounded flex items-center gap-1 z-10">
              <Play size={11} className="fill-white" /> Video
            </span>
          )}
          <div className="relative p-7 text-white">
            <span className="bg-sq-orange text-white text-[11px] uppercase tracking-wide font-semibold px-3 py-1">
              {lead.category}
            </span>
            <h1 className="font-serif-display text-3xl md:text-4xl leading-tight mt-3 max-w-2xl">
              {lead.title}
            </h1>
            <p className="text-gray-200 mt-3 max-w-xl hidden md:block">{lead.excerpt}</p>
            <span className="text-xs text-gray-300 mt-3 block">
              {lead.author} · {lead.date}
            </span>
          </div>
        </Link>
        <div className="flex flex-col gap-6">
          {secondary.map((a) => (
            <Link
              key={a.id}
              to={`/articolo/${a.slug}`}
              className="group relative rounded-sm overflow-hidden card-img-wrap flex-1 min-h-[168px] flex items-end"
            >
              <img
                src={fileUrl(a.image)}
                alt={a.title}
                className="card-img-zoom absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
              {a.videoUrl && (
                <span className="absolute top-3 left-3 bg-sq-orange text-white text-[10px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded flex items-center gap-1 z-10">
                  <Play size={9} className="fill-white" /> Video
                </span>
              )}
              <div className="relative p-4 text-white">
                <span className="text-[10px] uppercase tracking-wide text-sq-orange font-semibold">
                  {a.category}
                </span>
                <h2 className="font-serif-display text-lg leading-snug mt-1">{a.title}</h2>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <span className="sq-heading">Ultime notizie</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {firstHalf.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
          <div className="my-8 flex justify-center">
            <BannerAd id="mid-page" label="Spazio Pubblicitario" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {secondHalf.map((a) => (
              <ArticleCard key={a.id} article={a} />
            ))}
          </div>
        </div>
        <Sidebar />
      </div>
    </div>
  );
};

export default HomePage;
